import * as React from "react"
import { Link } from "react-router-dom"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Banknote, CalendarClock, Landmark, Plus, TriangleAlert } from "lucide-react"

import { DataState } from "@/components/DataState"
import { WorkPanel } from "@/components/WorkPanel"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Textarea } from "@/components/ui/textarea"
import { useToast } from "@/components/ui/use-toast"
import { useRbac } from "@/lib/auth"
import { formatAmount } from "@/lib/format"
import { cn } from "@/lib/utils"
import { onWriteError } from "@/lib/writeErrors"

import {
  cotizacionesSospechosas,
  leerCobertura,
  parsearCotizaciones,
  SOSPECHOSO_POR_DEBAJO_DE,
} from "@/routes/finance/datos"
import {
  asignarSociedad,
  cargarTiposDeCambio,
  crearBanco,
  fetchBancos,
  fetchTiposDeCambio,
  type CuentaBancaria,
} from "@/routes/finance/datosApi"
import { fetchSociedades, type SociedadMaestro } from "@/routes/masters/api"
import { t } from "@/lib/i18n"

/**
 * BANCOS Y TIPOS DE CAMBIO. Los dos datos de base que solo entraban por
 * `seeds/`, que leen la hoja de Drive de UN cliente.
 *
 * POR QUE ESTAN JUNTOS. Sin bancos no hay tesoreria ni runway —y el runway por
 * sociedad es el numero que decide si una empresa puede pagar su planilla—; sin
 * tipos de cambio el consolidado no se puede calcular en absoluto y los
 * umbrales en moneda extranjera se van a revision humana uno por uno. Son las
 * dos cosas que hay que tener cargadas antes de que ninguna cifra de grupo
 * signifique nada.
 *
 * LO QUE ESTA PANTALLA VIGILA, que es distinto de lo que carga:
 *
 * * **Una cuenta sin sociedad** suma al total del grupo y a la caja de nadie.
 *   No descuadra: desaparece del runway de la sociedad que la tiene.
 * * **Una moneda que se dejo de cotizar** sigue dando consolidados, calculados
 *   a la ultima fecha que se cargo, porque las consultas toman la cotizacion
 *   anterior a la fecha pedida —que para eso esta, los domingos no cotizan—.
 *   La columna «sin cotizar» es lo que convierte ese silencio en un aviso.
 * * **Una cotizacion cargada al reves** —dolares por sol en vez de soles por
 *   dolar— da un consolidado catorce veces mas grande, y es plausible si nadie
 *   se sabe las cifras de memoria. Se avisa antes de escribir; no se rechaza,
 *   porque el dia que entre una moneda que cotice por encima del dolar
 *   rechazarla seria el defecto.
 */
export function DatosBasePage() {
  return (
    <div className="space-y-3">
      <Bancos />
      <TiposDeCambio />
    </div>
  )
}

// ===========================================================================
// Cuentas bancarias
// ===========================================================================

function Bancos() {
  const { can } = useRbac()
  const puedeEditar = can("edit")
  const q = useQuery({ queryKey: ["bancos"], queryFn: fetchBancos })
  const sociedades = useQuery({ queryKey: ["sociedades"], queryFn: fetchSociedades })
  const [alta, setAlta] = React.useState(false)

  const sinSociedad = (q.data ?? []).filter((c) => !c.entity_id).length
  const sinMoneda = (q.data ?? []).filter((c) => !c.moneda).length

  return (
    <>
      <DataState
        loading={q.isLoading}
        error={q.error}
        empty={!q.isLoading && !q.error && (q.data?.length ?? 0) === 0}
        emptyMessage={t("No hay cuentas bancarias cargadas.")}
        emptyHint={t("Sin bancos no hay tesorería ni runway, y el runway por sociedad es el número que decide si una empresa puede pagar su planilla. Añade una aquí, o súbelas todas desde Maestros › Carga por archivo.")}
        emptyAction={
          puedeEditar ? (
            <Button size="sm" onClick={() => setAlta(true)}>
              <Plus />
              {t("Nueva cuenta")}</Button>
          ) : null
        }
        onRetry={() => void q.refetch()}
        columns={[28, 10, 18, 24, 20]}
      >
        <WorkPanel
          title={t("Cuentas bancarias")}
          meta={
            <>
              <Badge variant="outline">{q.data?.length ?? 0} cuentas</Badge>
              {sinSociedad > 0 ? (
                <Badge variant="warning-soft" estado>
                  {sinSociedad}{" "}{t("sin sociedad")}</Badge>
              ) : null}
              {sinMoneda > 0 ? (
                <Badge variant="destructive-soft" estado>
                  {sinMoneda}{" "}{t("sin moneda")}</Badge>
              ) : null}
            </>
          }
          actions={
            <>
              <Button size="xs" variant="ghost" asChild>
                <Link to="/contabilidad/maestros/movimientos-bancarios">{t("Movimientos")}</Link>
              </Button>
              {puedeEditar ? (
                <Button size="xs" variant="outline" onClick={() => setAlta((v) => !v)}>
                  <Plus />
                  {alta ? "Cancelar" : "Nueva cuenta"}
                </Button>
              ) : null}
            </>
          }
          footnote={t("Una cuenta sin sociedad suma al total del grupo y a la caja de nadie: no descuadra ningún número, simplemente desaparece del runway de la sociedad que la tiene. Una sin moneda no entra en ningún subtotal, porque convertirla exigiría inventarle una.")}
        >
          {alta ? (
            <AltaDeCuenta sociedades={sociedades.data ?? []} onListo={() => setAlta(false)} />
          ) : null}
          <Table className="table-dense">
            <TableHeader>
              <TableRow>
                <TableHead className="w-[18rem]">{t("Cuenta")}</TableHead>
                <TableHead className="w-20">{t("Moneda")}</TableHead>
                <TableHead className="w-36 text-right">{t("Saldo")}</TableHead>
                <TableHead>{t("Sociedad")}</TableHead>
                <TableHead className="w-24">{t("Origen")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(q.data ?? []).map((c) => (
                <FilaDeCuenta
                  key={c.id}
                  cuenta={c}
                  sociedades={sociedades.data ?? []}
                  puedeEditar={puedeEditar}
                />
              ))}
            </TableBody>
          </Table>
        </WorkPanel>
      </DataState>
    </>
  )
}

function FilaDeCuenta({
  cuenta,
  sociedades,
  puedeEditar,
}: {
  cuenta: CuentaBancaria
  sociedades: SociedadMaestro[]
  puedeEditar: boolean
}) {
  const [abierto, setAbierto] = React.useState(false)
  const [entityId, setEntityId] = React.useState("")
  const [motivo, setMotivo] = React.useState("")
  const { toast } = useToast()
  const qc = useQueryClient()

  const asignar = useMutation({
    mutationFn: () => asignarSociedad(cuenta.id, { entity_id: entityId, motivo }),
    onSuccess: (r) => {
      toast({ title: `${r.nombre} asignada`, description: r.aviso ?? undefined })
      setAbierto(false)
      setMotivo("")
      void qc.invalidateQueries({ queryKey: ["bancos"] })
      // La caja por sociedad y el runway cambian con esto.
      void qc.invalidateQueries({ queryKey: ["tesoreria"] })
      void qc.invalidateQueries({ queryKey: ["consolidado"] })
    },
    onError: onWriteError("la cuenta", "asignar"),
  })

  const listo = entityId.length > 0 && motivo.trim().length >= 3

  return (
    <>
      <TableRow>
        <TableCell className="truncate font-medium" title={cuenta.nombre}>
          {cuenta.nombre}
        </TableCell>
        <TableCell className="code">
          {cuenta.moneda ?? (
            <span className="text-2xs text-destructive">{t("sin moneda")}</span>
          )}
        </TableCell>
        <TableCell
          className={cn("num", !cuenta.saldo_final && "num-zero")}
          title={t("Saldo final, en la moneda de la cuenta. Aquí no se convierte nada.")}
        >
          {cuenta.saldo_final === null ? "—" : formatAmount(Number(cuenta.saldo_final))}
        </TableCell>
        <TableCell>
          <div className="flex flex-wrap items-center gap-2">
            {cuenta.razon_social ? (
              <span className="truncate" title={cuenta.razon_social}>
                {cuenta.razon_social}
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-2xs text-warning">
                <TriangleAlert className="h-3 w-3" />
                {t("Sin asignar — entra en el total del grupo y en la caja de nadie")}</span>
            )}
            {puedeEditar ? (
              <Button size="xs" variant="ghost" onClick={() => setAbierto((v) => !v)}>
                {abierto ? "Cancelar" : cuenta.entity_id ? "Cambiar" : "Asignar"}
              </Button>
            ) : null}
          </div>
        </TableCell>
        <TableCell className="code text-2xs text-muted-foreground">
          {cuenta.origen ?? "—"}
        </TableCell>
      </TableRow>

      {abierto ? (
        <TableRow>
          <TableCell colSpan={5} className="bg-surface-sunken">
            <div className="animate-row-in grid gap-3 px-1 py-2 md:grid-cols-[minmax(0,18rem)_minmax(0,1fr)_auto] md:items-end">
              <div className="space-y-1">
                <Label htmlFor={`soc-${cuenta.id}`}>{t("Sociedad")}</Label>
                <select
                  id={`soc-${cuenta.id}`}
                  value={entityId}
                  onChange={(e) => setEntityId(e.target.value)}
                  className={cn(
                    "h-7 w-full rounded-md border bg-surface px-2 text-xs",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                  )}
                >
                  <option value="">{t("Elige la sociedad…")}</option>
                  {sociedades.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.razon_social} · {s.pais}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-1">
                <Label htmlFor={`mot-${cuenta.id}`}>{t("Motivo *")}</Label>
                <Textarea
                  id={`mot-${cuenta.id}`}
                  value={motivo}
                  onChange={(e) => setMotivo(e.target.value)}
                  rows={2}
                  placeholder={t("Cómo se comprobó que esta cuenta es de esa sociedad.")}
                  className="text-xs"
                />
              </div>
              <Button size="sm" disabled={!listo || asignar.isPending} onClick={() => asignar.mutate()}>
                <Landmark />
                {asignar.isPending ? "Asignando…" : "Asignar"}
              </Button>
            </div>
            <p className="px-1 pb-2 text-2xs leading-4 text-muted-foreground">
              {t("Asignar mal pone la caja de una sociedad en otra")}{" "}
              <strong className="font-medium">{t("y el total del grupo sigue cuadrando")}</strong>{t(": no hay ninguna cifra que se descuadre para avisarlo. Por eso exige motivo.")}</p>
          </TableCell>
        </TableRow>
      ) : null}
    </>
  )
}

function AltaDeCuenta({
  sociedades,
  onListo,
}: {
  sociedades: SociedadMaestro[]
  onListo: () => void
}) {
  const [nombre, setNombre] = React.useState("")
  const [moneda, setMoneda] = React.useState("")
  const [entityId, setEntityId] = React.useState("")
  const [saldo, setSaldo] = React.useState("")
  const [motivo, setMotivo] = React.useState("")
  const { toast } = useToast()
  const qc = useQueryClient()

  const crear = useMutation({
    mutationFn: () =>
      crearBanco({
        nombre,
        moneda,
        entity_id: entityId || null,
        saldo_final: saldo.trim() === "" ? null : Number(saldo),
        motivo,
      }),
    onSuccess: (r) => {
      toast({ title: `${r.nombre} creada`, description: r.aviso ?? undefined })
      setNombre("")
      setMoneda("")
      setEntityId("")
      setSaldo("")
      setMotivo("")
      void qc.invalidateQueries({ queryKey: ["bancos"] })
      void qc.invalidateQueries({ queryKey: ["tesoreria"] })
      onListo()
    },
    onError: onWriteError("la cuenta bancaria", "crear"),
  })

  const listo =
    nombre.trim().length >= 2 && moneda.trim().length === 3 && motivo.trim().length >= 3

  return (
    <div className="space-y-2 border-b bg-surface-sunken/40 p-2.5">
      <div className="grid gap-2 md:grid-cols-4">
        <div className="space-y-1 md:col-span-2">
          <Label htmlFor="cuenta-nombre">{t("Nombre de la cuenta *")}</Label>
          <Input
            id="cuenta-nombre"
            value={nombre}
            onChange={(e) => setNombre(e.target.value)}
            placeholder={t("BCP Cuenta Corriente MN")}
            className="text-xs"
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="cuenta-moneda">{t("Moneda *")}</Label>
          <Input
            id="cuenta-moneda"
            value={moneda}
            maxLength={3}
            onChange={(e) => setMoneda(e.target.value.toUpperCase())}
            placeholder={t("PEN")}
            className="code text-xs"
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="cuenta-saldo">{t("Saldo actual")}</Label>
          <Input
            id="cuenta-saldo"
            value={saldo}
            onChange={(e) => setSaldo(e.target.value)}
            inputMode="decimal"
            placeholder="125430.50"
            className="num text-xs"
          />
        </div>
      </div>
      <div className="grid gap-2 md:grid-cols-[minmax(0,18rem)_minmax(0,1fr)_auto] md:items-end">
        <div className="space-y-1">
          <Label htmlFor="cuenta-sociedad">{t("Sociedad")}</Label>
          <select
            id="cuenta-sociedad"
            value={entityId}
            onChange={(e) => setEntityId(e.target.value)}
            className={cn(
              "h-7 w-full rounded-md border bg-surface px-2 text-xs",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            )}
          >
            <option value="">{t("Sin asignar (se puede decidir después)")}</option>
            {sociedades.map((s) => (
              <option key={s.id} value={s.id}>
                {s.razon_social} · {s.pais}
              </option>
            ))}
          </select>
        </div>
        <div className="space-y-1">
          <Label htmlFor="cuenta-motivo">{t("Motivo *")}</Label>
          <Textarea
            id="cuenta-motivo"
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            rows={2}
            className="text-xs"
            placeholder={t("Por qué se da de alta. Queda en la pista de auditoría.")}
          />
        </div>
        <Button size="sm" disabled={!listo || crear.isPending} onClick={() => crear.mutate()}>
          <Banknote />
          {crear.isPending ? "Creando…" : "Crear cuenta"}
        </Button>
      </div>
      <p className="text-2xs leading-4 text-muted-foreground">
        {t("Dejar la sociedad sin asignar es válido: hay cuentas —una pasarela de cobro en dólares, por ejemplo— que de verdad no son de una sola sociedad hasta que alguien lo decide. La tesorería las muestra aparte, con su aviso.")}</p>
    </div>
  )
}

// ===========================================================================
// Tipos de cambio
// ===========================================================================

function TiposDeCambio() {
  const { can } = useRbac()
  const puedeEditar = can("edit")
  const q = useQuery({ queryKey: ["tipos-cambio"], queryFn: () => fetchTiposDeCambio() })
  const [texto, setTexto] = React.useState("")
  const [motivo, setMotivo] = React.useState("")
  const { toast } = useToast()
  const qc = useQueryClient()

  const { filas, errores } = React.useMemo(() => parsearCotizaciones(texto), [texto])
  const sospechosas = React.useMemo(() => cotizacionesSospechosas(filas), [filas])
  const cobertura = React.useMemo(
    () => leerCobertura(q.data?.cobertura ?? [], new Date()),
    [q.data],
  )

  const cargar = useMutation({
    mutationFn: () => cargarTiposDeCambio({ tipos: filas, motivo }),
    onSuccess: (r) => {
      toast({
        title: `${r.creados} cotizaciones nuevas, ${r.actualizados} actualizadas`,
        description: r.rechazados.length
          ? `${r.rechazados.length} rechazadas: ${r.rechazados[0]}`
          : undefined,
      })
      setTexto("")
      setMotivo("")
      void qc.invalidateQueries({ queryKey: ["tipos-cambio"] })
      // Todo lo que se convierte a una moneda de presentación cambia.
      void qc.invalidateQueries({ queryKey: ["tesoreria"] })
      void qc.invalidateQueries({ queryKey: ["consolidado"] })
    },
    onError: onWriteError("los tipos de cambio", "cargar"),
  })

  const listo = puedeEditar && filas.length > 0 && motivo.trim().length >= 3

  return (
    <WorkPanel
      title={t("Tipos de cambio")}
      meta={
        <>
          <Badge variant="outline">{cobertura.length} monedas</Badge>
          {cobertura.some((c) => c.vencida) ? (
            <Badge variant="warning-soft" estado>
              <CalendarClock />
              {cobertura.filter((c) => c.vencida).length}{" "}{t("sin cotizar hace más de un mes")}</Badge>
          ) : null}
        </>
      }
      footnote={
        q.data?.convencion ??
        "Unidades de la moneda por UN dólar. El dólar no lleva fila porque valdría 1."
      }
    >
      <DataState
        loading={q.isLoading}
        error={q.error}
        empty={!q.isLoading && !q.error && (q.data?.cobertura.length ?? 0) === 0}
        emptyMessage={t("No hay ningún tipo de cambio cargado.")}
        emptyHint={t("Sin ellos el consolidado no se puede calcular en absoluto, y los umbrales en moneda extranjera se van a revisión uno por uno. Pega las cotizaciones abajo.")}
        onRetry={() => void q.refetch()}
        columns={[20, 20, 30, 30]}
      >
        <Table className="table-dense">
          <TableHeader>
            <TableRow>
              <TableHead className="w-20">{t("Moneda")}</TableHead>
              <TableHead className="w-24 text-right">{t("Filas")}</TableHead>
              <TableHead className="w-40">{t("Desde")}</TableHead>
              <TableHead className="w-40">{t("Hasta")}</TableHead>
              <TableHead>{t("Sin cotizar")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {cobertura.map((c) => (
              <TableRow key={c.moneda}>
                <TableCell className="code font-medium">{c.moneda}</TableCell>
                <TableCell className="num">{c.filas}</TableCell>
                <TableCell className="code text-muted-foreground">{c.desde}</TableCell>
                <TableCell className="code">{c.hasta}</TableCell>
                <TableCell>
                  {c.vencida ? (
                    <span className="inline-flex items-center gap-1 text-2xs text-warning">
                      <TriangleAlert className="h-3 w-3" />
                      {c.diasSinCotizar}{" "}{t("días. El consolidado sigue saliendo, calculado a esa fecha, y nada avisa.")}</span>
                  ) : (
                    <span className="text-2xs text-muted-foreground">
                      {c.diasSinCotizar} {c.diasSinCotizar === 1 ? "día" : "días"}
                    </span>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </DataState>

      {puedeEditar ? (
        <div className="space-y-2 border-t bg-surface-sunken/40 p-2.5">
          <div className="space-y-1">
            <Label htmlFor="fx-pegar">{t("Pegar cotizaciones")}</Label>
            <Textarea
              id="fx-pegar"
              value={texto}
              onChange={(e) => setTexto(e.target.value)}
              rows={4}
              className="code text-xs"
              placeholder={"2026-07-31\tPEN\t3,752\n2026-07-31\tMXN\t17,20"}
            />
            <p className="text-2xs leading-4 text-muted-foreground">
              {t("Tres columnas: fecha, moneda y cotización. Se puede pegar directamente desde la hoja de cálculo (tabuladores), o escribir con punto y coma. Recargar una fecha ya cargada la ACTUALIZA: dos filas del mismo día dejarían el tipo de cambio a criterio del planificador de consultas.")}</p>
          </div>

          {filas.length > 0 || errores.length > 0 ? (
            <div className="flex flex-wrap items-center gap-1.5">
              <Badge variant={errores.length ? "warning-soft" : "success-soft"} estado>
                {filas.length}{" "}{t("para cargar")}</Badge>
              {errores.length > 0 ? (
                <Badge variant="destructive-soft" estado>
                  {errores.length}{" "}{t("con error")}</Badge>
              ) : null}
            </div>
          ) : null}

          {errores.length > 0 ? (
            <ul className="space-y-0.5 rounded-md border border-destructive/25 bg-destructive-soft/30 p-2">
              {errores.slice(0, 10).map((e) => (
                <li key={`${e.linea}-${e.texto}`} className="text-2xs leading-4">
                  <span className="code font-medium">{t("Línea")}{" "}{e.linea}</span> · «{e.texto}»{" "}
                  {e.motivo}
                </li>
              ))}
              {errores.length > 10 ? (
                <li className="text-2xs text-muted-foreground">y {errores.length - 10}{" "}{t("más.")}</li>
              ) : null}
            </ul>
          ) : null}

          {sospechosas.length > 0 ? (
            <p className="rounded-md border border-warning/30 bg-warning-soft/40 px-2 py-1.5 text-2xs leading-4 text-foreground">
              <strong className="font-medium">
                {sospechosas.length}{" "}
                {sospechosas.length === 1 ? "cotización parece" : "cotizaciones parecen"}{" "}{t("ir al revés")}</strong>{" "}
              {t("(por debajo de")}{" "}{SOSPECHOSO_POR_DEBAJO_DE}):{" "}
              <span className="code">
                {sospechosas
                  .slice(0, 3)
                  .map((s) => `${s.moneda} ${s.tipo_cambio}`)
                  .join(", ")}
              </span>
              {t(". La convención es unidades por UN dólar: el sol ronda 3,7 y el peso 17. Cargarlo al revés no da ningún error — da un consolidado catorce veces más grande. Si es correcto, cárgalo igual.")}</p>
          ) : null}

          <div className="grid gap-2 md:grid-cols-[minmax(0,1fr)_auto] md:items-end">
            <div className="space-y-1">
              <Label htmlFor="fx-motivo">{t("Motivo *")}</Label>
              <Textarea
                id="fx-motivo"
                value={motivo}
                onChange={(e) => setMotivo(e.target.value)}
                rows={2}
                className="text-xs"
                placeholder={t("De dónde salen estas cotizaciones. Queda en la pista de auditoría.")}
              />
            </div>
            <Button size="sm" disabled={!listo || cargar.isPending} onClick={() => cargar.mutate()}>
              <CalendarClock />
              {cargar.isPending ? "Cargando…" : `Cargar ${filas.length}`}
            </Button>
          </div>
        </div>
      ) : null}
    </WorkPanel>
  )
}
